import {
  computed, onBeforeUnmount, ref, watch,
} from 'vue';
import axios from 'axios';
import { coachFeedbackState, LEGACY_WAIT_MS, PENDING_MAX_MS } from '../utils/coach_feedback';

// The match coach card's state and its Try again, shared by the classic and v5 cards.
// props: { feedback, feedbackState, accessLevel, matchId, viewerPlayed? }. Emits 'requested'
// when the server took the request (202) or has one in flight (429); the page then sets the
// match pending. Two client timers keep the card from waiting forever:
// - legacyWaiting: no usable state (older backend): pending for LEGACY_WAIT_MS, then quiet
// - stalled: pending for PENDING_MAX_MS with no answer (a lost cable event, a 429 with no job
//   behind it): shown as failed, so Try again comes back
export default function useMatchFeedback(props, emit) {
  const requesting = ref(false);
  const requestFailed = ref(false);
  const legacyWaiting = ref(true);
  const stalled = ref(false);
  let legacyTimer = null;
  let stallTimer = null;

  function restartTimers() {
    legacyWaiting.value = true;
    stalled.value = false;
    clearTimeout(legacyTimer);
    clearTimeout(stallTimer);
    legacyTimer = setTimeout(() => { legacyWaiting.value = false; }, LEGACY_WAIT_MS);
    stallTimer = setTimeout(() => { stalled.value = true; }, PENDING_MAX_MS);
  }
  // Any answer from the server starts the wait over
  watch(
    [() => props.feedbackState, () => props.feedback?.feedback_text],
    restartTimers,
    { immediate: true },
  );
  onBeforeUnmount(() => {
    clearTimeout(legacyTimer);
    clearTimeout(stallTimer);
  });

  const display = computed(() => coachFeedbackState({
    feedback: props.feedback,
    feedbackState: props.feedbackState,
    accessLevel: props.accessLevel,
    viewerPlayed: props.viewerPlayed ?? true,
    legacyWaiting: legacyWaiting.value,
    stalled: stalled.value,
  }));
  const canRetry = computed(() => ['failed', 'quiet'].includes(display.value));

  function accepted() {
    restartTimers();
    emit('requested');
  }

  async function requestFeedback() {
    if (requesting.value || !props.matchId) return;
    requesting.value = true;
    requestFailed.value = false;
    try {
      await axios.post(`/coach/matches/${props.matchId}/feedback`);
      accepted();
    } catch (error) {
      if (error?.response?.status === 429) accepted();
      else requestFailed.value = true;
    } finally {
      requesting.value = false;
    }
  }

  return {
    display, canRetry, requesting, requestFailed, requestFeedback,
  };
}
